import { lazy, type ComponentType } from "react"
import { i18n } from "./index"

type ComponentLoader<P = object> = () => Promise<{ default: ComponentType<P> }>
type NamespaceLoader = () => Promise<{ default: Record<string, unknown> }>
interface NamespaceTranslations {
  en: NamespaceLoader
  fr: NamespaceLoader
}

async function registerNamespace(
  namespace: string,
  translations: NamespaceTranslations,
): Promise<void> {
  const [en, fr] = await Promise.all([translations.en(), translations.fr()])
  if (!i18n.hasResourceBundle("en", namespace)) {
    i18n.addResourceBundle("en", namespace, en.default)
  }
  if (!i18n.hasResourceBundle("fr", namespace)) {
    i18n.addResourceBundle("fr", namespace, fr.default)
  }
}

/**
 * Registers a feature's translation namespace alongside its route chunk, so
 * translations load LAZILY WITH THE ROUTE rather than living in the shell --
 * the sign-in screen must not ship the administration vocabulary (LLD §10.1).
 *
 * Call once per screen that introduces a namespace; idempotent, so multiple
 * screens in one slice sharing a namespace do not re-register it.
 *
 * `extra` is for a screen that embeds ANOTHER slice's component (the
 * `RefundRequestDialog` cross-slice pattern, LLD
 * §1.1) -- that component's own `useTranslation` call reads a namespace this
 * route never otherwise loads, since it is reached without ever visiting the
 * owning slice's own routes. Omitted, that namespace's strings render as raw
 * keys the first time someone opens the embedding screen directly.
 */
export function withNamespace<P = object>(
  namespace: string,
  component: ComponentLoader<P>,
  translations: NamespaceTranslations,
  extra: { namespace: string; en: NamespaceLoader; fr: NamespaceLoader }[] = [],
): Preloadable<P> {
  let pending: Promise<{ default: ComponentType<P> }> | undefined
  const load = () => {
    pending ??= Promise.all([
      component(),
      registerNamespace(namespace, translations),
      ...extra.map((e) => registerNamespace(e.namespace, e)),
    ]).then(([mod]) => mod)
    return pending
  }
  return Object.assign(lazy(load), { preload: load })
}

/**
 * A lazy screen that can start its chunk and namespace before it renders
 * (Task 7.2: a returning user's boot fetches the screen the address names
 * while the session is restored). `lazy` then finds the same promise.
 */
export type Preloadable<P> = ReturnType<typeof lazy<ComponentType<P>>> & {
  preload: () => Promise<unknown>
}
